/* istanbul ignore file */
import 'isomorphic-fetch';

// ECDEX-3738: local copy of the Bootstrap rules the app uses, loaded alongside
// the CDN stylesheet (identical rules, so nothing changes yet). It must stay
// before main.scss: main.scss redefines some of these classes and has to keep
// winning the cascade.
import './scss/bootstrap-compat/spa.scss';
import './scss/cp-classes/spa.scss';
import './scss/main.scss';

// Replaces `@international/mastodon-i18n`'s dynamic `require()` of locale
// JSON files with statically-bundled, pre-registered locales. Must be
// imported before any component that calls `i18n.getCountries` /
// `i18n.getCountry` (the international login form, the report screenings
// helpers, etc.) — which is everything, so it goes here at the top of
// the entry module.
import './lib/i18nCountriesPolyfill';

// Same Vite/esbuild dynamic-`require` breakage as the countries polyfill,
// but for `i18n.getMomentLocale()` — without it every localized date
// pattern falls back to English. Pairs with the `moment` ->
// `moment-with-locales` alias in `vite.config.mts` (locale data) and must
// also load before `initializeTranslations()` sets the moment locale.
import './lib/momentLocalePolyfill';

import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { RouterProvider as Router } from 'react-router-dom';

import Providers from './providers';
import initializeTranslations from './initializeTranslations';
import initializeSentry from './sentry';
import initializeDataDog from './initializeDD';
import mountQueryDevtools from './queryDevtools';
import createClientRoutes from './routes/clientRoutes';

// `initializeSentry()` MUST run before `createClientRoutes()` so that
// `Sentry.wrapCreateBrowserRouter` can attach the React Router v6
// browser-tracing integration to the router instance it creates. If the
// router were created at module-import time it would race ahead of
// `Sentry.init()` and matched route patterns would not be attached to
// error events.
initializeSentry();
initializeDataDog();
initializeTranslations();

const clientRoutes = createClientRoutes();

const rootContainer = document.getElementById('root');

if (rootContainer) {
  const app = (
    <Providers>
      <Router router={clientRoutes} />
    </Providers>
  );

  // SSG-pre-rendered routes (`/login`, `/international/login`,
  // `/authenticate-candidate`) ship HTML inside `<div id="root">`, so we
  // hydrate. Every other route receives an empty `#root` and mounts fresh.
  if (rootContainer.hasChildNodes()) {
    hydrateRoot(rootContainer, app);
  } else {
    createRoot(rootContainer).render(app);
  }

  mountQueryDevtools();
}
